import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { audio } from '@/audio/AudioService';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { iapAvailable, initIap, getGemPackages, buyPackage, type PurchasesPackage } from './iapService';
import { fetchIapProducts, verifyIapPurchase, type IapProduct } from './iapApi';

interface GemBundle {
  pkg: PurchasesPackage;
  product: IapProduct;
}

export function GemsShopSection() {
  const setProfile = useUserStore((s) => s.setProfile);
  const userId = useAuthStore((s) => s.user?.id ?? '');

  const [bundles, setBundles] = useState<GemBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; ok: boolean } | null>(null);

  const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Always fetch server products first
        const products = await fetchIapProducts();
        if (cancelled) return;

        let pkgs: PurchasesPackage[] = [];
        if (iapAvailable()) {
          try {
            await initIap(userId);
            pkgs = await getGemPackages();
          } catch (e) {
            console.warn('[iap] rc init/packages failed', e);
          }
        }

        if (cancelled) return;
        const matched: GemBundle[] = pkgs
          .map((pkg) => {
            // Match by App Store productId or the Play-specific androidProductId
            // (Play SKUs can diverge when a deleted id can't be reused).
            const id = pkg.product.identifier;
            const product = products.find(
              (p) => p.productId === id || p.androidProductId === id,
            );
            return product ? { pkg, product } : null;
          })
          .filter(Boolean) as GemBundle[];

        // Show matched RC packages, or fallback to server products (buy disabled)
        setBundles(matched.length > 0 ? matched : products.map((p) => ({ pkg: null as any, product: p })));
      } catch (e) {
        console.warn('[iap] load', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const showToast = (text: string, ok: boolean) => {
    setToast({ text, ok });
    window.setTimeout(() => setToast(null), 2600);
  };

  const onBuy = async (bundle: GemBundle) => {
    if (busyId || !bundle.pkg) return;
    setBusyId(bundle.product.productId);
    try {
      const result = await buyPackage(bundle.pkg);
      audio.play('ui.shop_purchase');
      const updated = await verifyIapPurchase({
        transactionId: result.transactionId,
        productId: result.productId,
        platform: platform as 'ios' | 'android',
        rcUserId: result.rcUserId,
      });
      setProfile({ gems: updated.gems, coins: updated.coins });
      const total = bundle.product.rewardAmount + bundle.product.bonusAmount;
      showToast(`+${total} 💎 ${bundle.product.name}`, true);
    } catch (e: any) {
      if (e?.message?.includes('userCancelled') || e?.code === 1) return;
      showToast(e?.message ?? 'Purchase failed', false);
    } finally {
      setBusyId(null);
    }
  };

  if (!iapAvailable() && platform !== 'web') {
    return (
      <div style={{ padding: 20, color: 'var(--ink-muted)', fontSize: 12, textAlign: 'center' }}>
        In-app purchases not available on this device.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 20, color: 'var(--ink-muted)', fontSize: 12, textAlign: 'center' }}>
        Loading…
      </div>
    );
  }

  if (bundles.length === 0) {
    return (
      <div style={{ padding: 20, color: 'var(--ink-muted)', fontSize: 12, textAlign: 'center' }}>
        No gem packages available yet.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 12 }}>
        Gems are used to buy heroes, frames, and cosmetics.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bundles.map((b) => {
          const total = b.product.rewardAmount + b.product.bonusAmount;
          const price = b.pkg
            ? (b.pkg.product as any).priceString ?? `$${b.product.priceUsd ?? '?'}`
            : `$${b.product.priceUsd ?? '?'}`;
          const busy = busyId === b.product.productId;

          return (
            <div
              key={b.product.productId}
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 14px',
                gap: 14,
                borderColor: 'rgba(34,211,238,0.25)',
              }}
            >
              {/* Badge */}
              {b.product.badge && (
                <span
                  className="tag"
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 10,
                    fontSize: 8,
                    color: 'var(--c-yellow)',
                  }}
                >
                  {b.product.badge}
                </span>
              )}

              {/* Gem icon + amount */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
                <div style={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {resolveAssetUrl(b.product.imageUrl)
                    ? <img src={resolveAssetUrl(b.product.imageUrl)!} alt={b.product.name} style={{ width: 56, height: 56, objectFit: 'contain' }} />
                    : <span style={{ fontSize: 32, lineHeight: 1 }}>💎</span>}
                </div>
                <span
                  style={{
                    fontFamily: 'var(--f-display)',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'var(--c-cyan)',
                    marginTop: 2,
                  }}
                >
                  {total.toLocaleString()}
                </span>
                {b.product.bonusAmount > 0 && (
                  <span style={{ fontSize: 9, color: 'var(--c-yellow)', marginTop: 1 }}>
                    +{b.product.bonusAmount} BONUS
                  </span>
                )}
              </div>

              {/* Name */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{b.product.name}</div>
                {b.product.description && (
                  <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 2 }}>
                    {b.product.description}
                  </div>
                )}
              </div>

              {/* Buy button */}
              <button
                onClick={() => onBuy(b)}
                disabled={busy || !b.pkg}
                style={{
                  padding: '9px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: busy || !b.pkg
                    ? 'rgba(255,255,255,0.06)'
                    : 'linear-gradient(180deg, var(--c-yellow), #d9a800)',
                  color: busy || !b.pkg ? 'var(--ink-dim)' : '#1e1b4b',
                  fontFamily: 'var(--f-display)',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: busy || !b.pkg ? 'not-allowed' : 'pointer',
                  minWidth: 72,
                  boxShadow: busy || !b.pkg ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.4), 0 3px 0 #8a6a00',
                  whiteSpace: 'nowrap',
                }}
              >
                {busy ? '…' : price}
              </button>
            </div>
          );
        })}
      </div>

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 30px)',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 18px',
            borderRadius: 10,
            background: toast.ok ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.ok ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.ok ? 'var(--c-cyan)' : 'var(--c-red)',
            fontSize: 13,
            zIndex: 99,
            backdropFilter: 'blur(6px)',
            whiteSpace: 'nowrap',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
